import { createAdminMcpSessionFromConfig } from './admin-mcp.js';
import { createApiClient } from './api.js';
import { hasCommerceMedia, mapCommerceMedia } from './commerce-media.js';
import { canSubmitLeadForm, createLeadFormMessage } from './lead-form.js';
import { resolveLinkTarget } from './link-target.js';
import { renderAssistantMessageHtml } from './markdown.js';
import { startProactiveInvitation } from './proactive.js';
import { subscribeConversationChannel } from './handover-echo.js';
import { consumeChatStream } from './sse.js';
import { detectHostDarkMode, subscribeHostTheme } from './theme.js';
import { createVoiceSession } from './voice.js';
import { createSilenceDetector } from './voice-vad.js';
import { createVoiceWsSession } from './voice-ws.js';
import {
    createVoicePipecatSession,
    preloadVoicePipecat,
    releaseWarmMicrophone,
    unlockVoiceRemoteSpeaker,
    warmUpVoiceMicrophone,
} from './voice-pipecat.js';
import {
    clearStoredConversationId,
    getOrCreateSessionId,
    getStoredConversationId,
    resetWidgetSession,
    setStoredConversationId,
} from './storage.js';

function mapHistoryMessage(message) {
    return {
        role: message.role === 'agent' ? 'agent' : message.role,
        content: message.content,
        id: message.id ?? null,
        feedback: message.feedback ?? null,
        sourceAttribution: message.source_attribution?.text ?? null,
        commerceMedia: mapCommerceMedia(message.commerce_media),
        agentName: message.metadata?.agent_name ?? null,
        leadForm: message.metadata?.lead_form ?? null,
    };
}

/**
 * Agent üzenethez tartozó lead_form metadata → widget kártya event.
 *
 * @param {object|null|undefined} leadForm
 * @param {string|number|null|undefined} messageId
 */
function leadFormEventFromMetadata(leadForm, messageId = null) {
    if (! leadForm || typeof leadForm !== 'object') {
        return null;
    }

    const formId = leadForm.form_id ?? null;
    const formPayload = leadForm.form_payload ?? null;

    if (! formId || ! formPayload || typeof formPayload !== 'object') {
        return null;
    }

    return {
        form_id: formId,
        form_payload: formPayload,
        source_message_id: messageId != null ? `lf_${messageId}` : null,
    };
}

/**
 * AgentMessageSent / history — lead_form nested vagy SSE-szerű top-level mezők.
 *
 * @param {object} event
 */
function leadFormEventFromAgentEvent(event) {
    if (! event || typeof event !== 'object') {
        return null;
    }

    const fromNested = leadFormEventFromMetadata(event.lead_form, event.message_id ?? event.id);
    if (fromNested) {
        return fromNested;
    }

    if (event.form_id && event.form_payload) {
        return leadFormEventFromMetadata(
            { form_id: event.form_id, form_payload: event.form_payload },
            event.message_id ?? event.id,
        );
    }

    return null;
}

function handoverBannerText(status, agentName = null) {
    if (status === 'pending_human') {
        return 'Egy munkatársunk hamarosan csatlakozik a beszélgetéshez…';
    }

    if (status === 'human_active') {
        return agentName
            ? `${agentName} csatlakozott a beszélgetéshez`
            : 'Ügyfélszolgálat csatlakozott a beszélgetéshez';
    }

    if (status === 'closed') {
        return 'A beszélgetés lezárult — folytathatod az AI asszisztenssel';
    }

    return null;
}

/**
 * @param {import('alpinejs').Alpine} alpine — host oldal Alpine-ja (pl. Filament), vagy a widget saját példánya
 */
export function registerChatWidget(alpine) {
    if (window.__chatbotWidgetRegistered) {
        return;
    }

    window.__chatbotWidgetRegistered = true;

    alpine.data('chatWidget', (botKey, apiBase, initialConfig) => ({
        botKey,
        apiBase,
        config: initialConfig,
        open: false,
        loading: false,
        streaming: false,
        error: null,
        /** Ha 429 limit-hiba jön, az upgrade URL-t tároljuk ide */
        upgradeUrl: null,
        input: '',
        messages: [],
        conversationId: null,
        broadcastAuthToken: null,
        reverbConfig: null,
        handoverSubscription: null,
        handoverPollTimer: null,
        /** Hanghívás közben: átvétel státusz poll (Pipecat app-message nélkül is) */
        voiceHandoverPollTimer: null,
        /** syncHandoverState reentrancy — apply → startPolling → sync kör elkerülése */
        _handoverSyncInFlight: false,
        handoverStatus: 'bot_active',
        handoverBanner: null,
        handoverAgentName: null,
        closeNoticeShown: false,
        /** EU AI Act — új session elején látható platform felirat */
        showAiDisclosure: false,

        get statusStripText() {
            if (this.handoverBanner) {
                return this.handoverBanner;
            }

            if (this.showAiDisclosure && this.config?.ai_disclosure) {
                return this.config.ai_disclosure;
            }

            return null;
        },

        get statusStripKind() {
            if (this.handoverBanner) {
                return 'handover';
            }

            if (this.showAiDisclosure && this.config?.ai_disclosure) {
                return 'ai';
            }

            return null;
        },

        api: null,
        adminMcpSession: null,
        voiceEnabled: initialConfig?.voice_enabled === true,
        voicePipecatEnabled: initialConfig?.voice_pipecat_enabled === true,
        voiceDebugTimings: initialConfig?.voice_debug_timings === true,
        /** Cache-elt köszönés — a kapcsolódás alatt szól, hogy „élő legyen a vonal” */
        voiceGreetingUrl: initialConfig?.voice_greeting_audio_url ?? null,
        voiceGreetingText: initialConfig?.voice_greeting_text ?? null,
        voiceGreetingAudio: null,
        voiceGreetingPlaying: false,
        /** @type {(() => void)|null} — a köszönés promise feloldója */
        _voiceGreetingDone: null,
        /** { t0, marks: [] } — kapcsolódás / első hang szakaszidők, csak debug módban */
        voiceTimings: null,
        voiceState: 'idle',
        voiceCallActive: false,
        voiceCallStartedAt: 0,
        /** true = unmute + line_open után — ekkor mehet a piros listening */
        _voiceLineOpen: false,
        /** Beragadt „gondolkodik” állapot őre — timeout id */
        voiceStuckTimer: null,
        voiceSession: null,
        voiceWsCall: null,
        voicePipecatCall: null,
        /** true = session API sikerült, concurrent slot foglalva → hangup-nál end kell */
        voicePipecatSessionOpen: false,
        voiceBookingSignal: null,
        voicePipecatToken: null,
        mediaRecorder: null,
        mediaStream: null,
        silenceDetector: null,
        voicePlayback: null,
        voiceAudioCtx: null,
        voiceSkipProcess: false,
        voiceChunks: [],
        voiceStartedAt: 0,
        hostIsDark: detectHostDarkMode(),
        themeUnsubscribe: null,
        /** Proaktív felkérés (teaser): szöveg, láthatóság és a triggerek leállítója. */
        proactiveMessage: '',
        proactiveVisible: false,
        proactiveStop: null,
        /** Küldés / aljra görgetés után követi a streamet; felfelé görgetéskor kikapcsol. */
        stickToBottom: false,

        get positionClass() {
            const pos = this.config?.appearance?.position ?? 'bottom_right';

            return pos === 'bottom_left' ? 'left' : 'right';
        },

        get primaryColor() {
            return this.config?.appearance?.primary_color ?? '#4F46E5';
        },

        get borderRadius() {
            return `${this.config?.appearance?.border_radius ?? 12}px`;
        },

        get showBranding() {
            return this.config?.appearance?.show_branding !== false;
        },

        get showSourceAttribution() {
            return this.config?.features?.show_source_attribution === true
                && this.config?.appearance?.show_source_attribution === true;
        },

        get avatarUrl() {
            return this.config?.appearance?.avatar_url ?? null;
        },

        get themeMode() {
            const appearance = this.config?.appearance;

            if (appearance?.theme_mode) {
                return appearance.theme_mode;
            }

            if (appearance?.dark_mode === true) {
                return 'dark';
            }

            if (appearance?.dark_mode === false) {
                return 'light';
            }

            return 'light';
        },

        get darkMode() {
            if (this.themeMode === 'dark') {
                return true;
            }

            if (this.themeMode === 'light') {
                return false;
            }

            return this.hostIsDark;
        },

        get appOrigin() {
            try {
                return new URL(this.apiBase).origin;
            } catch {
                return '#';
            }
        },

        get brandLogoLightUrl() {
            return `${this.appOrigin}/images/brand/chatt-widget-light.png`;
        },

        get brandLogoDarkUrl() {
            return `${this.appOrigin}/images/brand/chatt-widget-dark.png`;
        },

        get hasActiveConversation() {
            return this.conversationId !== null
                || this.messages.some((message) => message.role === 'user');
        },

        async init() {
            this.api = createApiClient(this.apiBase, this.botKey);
            this.adminMcpSession = createAdminMcpSessionFromConfig(this.config);
            this.voiceEnabled = this.config?.voice_enabled === true;
            this.voicePipecatEnabled = this.config?.voice_pipecat_enabled === true;
            this.voiceDebugTimings = this.config?.voice_debug_timings === true;
            this.voiceGreetingUrl = this.config?.voice_greeting_audio_url ?? null;
            this.voiceGreetingText = this.config?.voice_greeting_text ?? null;
            this.voiceSession = this.voiceEnabled ? createVoiceSession(this.api) : null;

            this.hostIsDark = detectHostDarkMode();
            this.themeUnsubscribe?.();
            this.themeUnsubscribe = subscribeHostTheme((isDark) => {
                this.hostIsDark = isDark;
            });
            window.__chatbotThemeUnsubscribe = this.themeUnsubscribe;
            await this.restoreConversationFromStorage();
            this.armProactiveInvitation();
        },

        destroy() {
            this.hangupVoiceCall(true);
            this.stopHandoverPolling();
            this.handoverSubscription?.leave?.();
            this.handoverSubscription = null;
            this.themeUnsubscribe?.();
            this.themeUnsubscribe = null;
            window.__chatbotThemeUnsubscribe = null;
            this.stopProactiveInvitation();
        },

        /** Csak friss, még nem beszélgető látogatónál kérünk fel proaktívan. */
        armProactiveInvitation() {
            if (this.open || this.hasActiveConversation || this.handoverStatus !== 'bot_active') {
                return;
            }

            this.proactiveStop = startProactiveInvitation({
                botKey: this.botKey,
                config: this.config?.appearance?.proactive ?? null,
                onShow: () => this.showProactive(),
            });
        },

        stopProactiveInvitation() {
            this.proactiveStop?.();
            this.proactiveStop = null;
        },

        showProactive() {
            const message = this.config?.appearance?.proactive?.message;

            if (this.open || ! message) {
                return;
            }

            this.proactiveMessage = message;
            this.proactiveVisible = true;
        },

        /** Bezárás után nincs újra-felugrás ebben a munkamenetben. */
        dismissProactive() {
            this.proactiveVisible = false;
            this.stopProactiveInvitation();
        },

        openFromProactive() {
            this.dismissProactive();
            this.toggle();
        },

        disableVoiceUi() {
            this.hangupVoiceCall(true);
            this.voiceEnabled = false;
            this.voicePipecatEnabled = false;
            this.voiceSession?.clear();
            this.voiceSession = null;
        },

        /** Csak hangperc / csomag limit — concurrency / átmeneti 429 nem kapcsolja ki a micet. */
        isVoicePlanUnavailable(err) {
            const msg = err?.data?.message ?? err?.message;

            return err?.status === 429 && msg === 'voice_unavailable';
        },

        releaseVoicePipecatSession() {
            if (! this.voicePipecatSessionOpen) {
                return;
            }
            const token = this.voicePipecatToken;
            this.voicePipecatSessionOpen = false;
            this.voicePipecatToken = null;
            this.api?.endVoicePipecatSession?.(token);
        },

        stopSilenceDetector() {
            this.silenceDetector?.stop();
            this.silenceDetector = null;
        },

        stopVoicePlayback() {
            if (this.voicePlayback) {
                try {
                    this.voicePlayback.pause();
                    this.voicePlayback.src = '';
                } catch {
                    // ignore
                }
                this.voicePlayback = null;
            }
        },

        preloadVoiceGreeting() {
            if (! this.voiceGreetingUrl || this.voiceGreetingAudio) {
                return;
            }
            try {
                const audio = new Audio(this.voiceGreetingUrl);
                audio.preload = 'auto';
                audio.load();
                this.voiceGreetingAudio = audio;
            } catch {
                this.voiceGreetingAudio = null;
            }
        },

        /**
         * Azonnali köszönés a kattintáskor. A visszaadott promise akkor teljesül,
         * amikor a hang lement — addig a mikrofon néma marad (nincs visszhang-turn).
         *
         * @param {string|null} urlOverride — session válaszból, ha a configban még nem volt URL
         * @param {Promise<unknown>|null} audioRouteReady — a mikrofon bekapcsolása; addig némán megy
         * @returns {Promise<void>}
         */
        playVoiceGreeting(urlOverride = null, audioRouteReady = null) {
            if (urlOverride) {
                this.voiceGreetingUrl = urlOverride;
                this.voiceGreetingAudio = null;
            }
            this.preloadVoiceGreeting();
            const audio = this.voiceGreetingAudio;
            if (! audio) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                let settled = false;
                const done = () => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    this.voiceGreetingPlaying = false;
                    this._voiceGreetingDone = null;
                    resolve();
                };

                // Korai leállításnál (stopVoiceGreeting) is fel kell oldani,
                // különben a mikrofon a timeoutig némán maradna.
                this._voiceGreetingDone = done;

                // Telefonon a mikrofon bekapcsolása hívás-hangmódba vált, ami levágja a hang elejét.
                // Ezért a kattintáskor halkan indul, és az átváltás után elölről szól.
                // Androidon nulla hangerő kell, nem `muted`: a némán indított hangot a Chrome
                // leállítja, ha később érintés nélkül visszakapcsoljuk. iOS-en a hangerő nem
                // állítható kódból, ott marad a `muted`.
                let warmingUp = Boolean(audioRouteReady);
                let silenceByMute = false;
                const silence = (quiet) => {
                    if (! quiet) {
                        audio.muted = false;
                        audio.volume = 1;

                        return;
                    }
                    audio.muted = false;
                    audio.volume = 0;
                    silenceByMute = audio.volume !== 0;
                    audio.muted = silenceByMute;
                };

                const onEnded = () => {
                    if (! warmingUp) {
                        audio.removeEventListener('ended', onEnded);
                        done();
                    }
                };
                audio.addEventListener('ended', onEnded);
                audio.addEventListener('error', done, { once: true });
                // Biztonsági háló: ha a lejátszás elakad, ne maradjon néma a mikrofon.
                setTimeout(done, 14000);

                try {
                    audio.currentTime = 0;
                    silence(warmingUp);
                    this.voiceGreetingPlaying = true;
                    const started = audio.play();
                    if (started?.catch) {
                        started.catch(done);
                    }
                } catch {
                    done();
                }

                if (warmingUp) {
                    const routeTimeout = new Promise((r) => setTimeout(r, 1500));
                    Promise.race([Promise.resolve(audioRouteReady).catch(() => null), routeTimeout])
                        // Androidos kihangosítón a hívás-módra váltás a mikrofon után még ~0,5 s; addig a hang eleje elvész.
                        .then(() => new Promise((r) => setTimeout(r, 700)))
                        .then(() => {
                            warmingUp = false;
                            if (settled) {
                                silence(false);

                                return;
                            }
                            try {
                                audio.currentTime = 0;
                                silence(false);
                                // Az átváltás megállíthatta a lejátszást — ugyanazt az elemet indítjuk újra.
                                if (audio.paused || silenceByMute) {
                                    const restarted = audio.play();
                                    if (restarted?.catch) {
                                        restarted.catch(done);
                                    }
                                }
                            } catch {
                                done();
                            }
                        });
                }
            });
        },

        stopVoiceGreeting() {
            this.voiceGreetingPlaying = false;
            const audio = this.voiceGreetingAudio;
            if (audio) {
                try {
                    audio.pause();
                    audio.currentTime = 0;
                    audio.muted = false;
                    audio.volume = 1;
                } catch {
                    // ignore
                }
            }
            this._voiceGreetingDone?.();
        },

        /**
         * Ha a bot nem szólal meg (hálózat, agent hiba), a mikrofon ne maradjon
         * örökre „gondolkodik” állapotban — a látogató tudjon újra beszélni.
         */
        armVoiceStuckWatchdog() {
            clearTimeout(this.voiceStuckTimer);
            this.voiceStuckTimer = null;

            if (this.voiceState !== 'processing') {
                return;
            }

            this.voiceStuckTimer = setTimeout(() => {
                if (this.voiceCallActive && this.voiceState === 'processing') {
                    this.voiceState = 'listening';
                }
            }, 20000);
        },

        startVoiceTimings() {
            if (! this.voiceDebugTimings) {
                return;
            }
            this.voiceTimings = { t0: performance.now(), marks: [] };
        },

        markVoiceTiming(label) {
            if (! this.voiceTimings) {
                return;
            }
            const ms = Math.round(performance.now() - this.voiceTimings.t0);
            this.voiceTimings.marks.push(`${label}=${ms}ms`);
            // eslint-disable-next-line no-console
            console.debug(`[chatt-voice] ${label} ${ms}ms`, this.voiceTimings.marks.join(' '));
        },

        async unlockAudioForIos() {
            try {
                const AudioCtx = window.AudioContext || window.webkitAudioContext;
                if (! AudioCtx) {
                    return;
                }
                if (! this.voiceAudioCtx) {
                    this.voiceAudioCtx = new AudioCtx();
                }
                if (this.voiceAudioCtx.state === 'suspended') {
                    await this.voiceAudioCtx.resume();
                }
            } catch {
                // ignore
            }
        },

        stopVoiceCapture(silent = false) {
            this.stopSilenceDetector();
            try {
                if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                    this.mediaRecorder.stop();
                }
            } catch {
                // ignore
            }
            this.mediaRecorder = null;
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach((t) => t.stop());
                this.mediaStream = null;
            }
            if (! silent && this.voiceState === 'listening') {
                this.voiceState = 'idle';
            }
        },

        hangupVoiceCall(silent = false) {
            clearTimeout(this.voiceStuckTimer);
            this.voiceStuckTimer = null;
            this.stopVoiceHandoverWatch();
            this.voiceCallActive = false;
            this.voiceCallStartedAt = 0;
            this._voiceLineOpen = false;
            this.voiceSkipProcess = true;
            this._voicePipecatGen = (this._voicePipecatGen || 0) + 1;
            this.voiceWsCall?.stop();
            this.voiceWsCall = null;
            const pipecat = this.voicePipecatCall;
            this.voicePipecatCall = null;
            if (pipecat) {
                Promise.resolve(pipecat.stop()).catch(() => {});
            }
            this.releaseVoicePipecatSession();
            this.stopVoiceGreeting();
            this.stopVoicePlayback();
            this.stopVoiceCapture(true);
            this.streaming = false;
            this.voiceState = 'idle';
            this.messages = this.messages.filter((m) => ! m.voiceStatus && ! (m.typing && ! m.content));
            if (! silent) {
                this.error = null;
            }
        },

        async toggleVoice() {
            if (! this.voiceEnabled) {
                return;
            }

            // Aktív hívás → leállítás. Az első 1,5 s véd a mobil dupla-tap ellen;
            // azon túl bármelyik állapotból le lehessen tenni (beragadt „gondolkodik” is).
            if (this.voiceCallActive || this.voicePipecatCall || this.voiceWsCall) {
                const startedAt = this.voiceCallStartedAt || 0;
                if (startedAt && (Date.now() - startedAt) < 1500) {
                    return;
                }
                this.hangupVoiceCall();

                return;
            }

            if (this.voiceState !== 'idle' && this.voiceState !== 'connecting') {
                this.voiceState = 'idle';
            }

            this.voiceCallActive = true;
            this.voiceCallStartedAt = Date.now();
            // Connecting + loader, amíg a vonal ténylegesen meg nem nyílik (line_open).
            this._voiceLineOpen = false;
            this.voiceState = 'connecting';
            this.error = null;
            this.startVoiceTimings();
            this.markVoiceTiming('click');
            this.startVoiceHandoverWatch();

            // A mikrofon engedély és a Daily SDK a session hívással párhuzamosan
            // melegszik be — a kattintás után semmi ne fusson feleslegesen sorban.
            let micTrackPromise = null;
            let greetingDone = null;
            if (this.voicePipecatEnabled) {
                preloadVoicePipecat();
                micTrackPromise = warmUpVoiceMicrophone();
                // Kattintás pillanata — még a user gesture-ön belül, await nélkül.
                unlockVoiceRemoteSpeaker();
                greetingDone = this.playVoiceGreeting(null, micTrackPromise);
            }

            // Prioritás: Pipecat Cloud (Daily) → local WS bridge → V1 HTTP kör
            // Pipecat bekapcsolva: TILOS V1 fallback (különben árva Daily + beragadt UI).
            try {
                await this.unlockAudioForIos();
                if (! this.conversationId) {
                    await this.initConversation();
                    this.markVoiceTiming('conversation');
                }
                // Conversation ID után azonnali státusz-check (átvétel már lehet folyamatban).
                this.pollVoiceHandoverState();

                if (this.voicePipecatEnabled) {
                    await this.startVoicePipecatCall(micTrackPromise, greetingDone);

                    return;
                }

                const session = await this.voiceSession?.ensureToken();
                const wsUrl = session?.voiceWsUrl;
                if (wsUrl) {
                    await this.startVoiceWsCall(wsUrl);

                    return;
                }
            } catch (err) {
                releaseWarmMicrophone(micTrackPromise);
                this.stopVoiceGreeting();

                if (this.isVoicePlanUnavailable(err)) {
                    this.disableVoiceUi();

                    return;
                }

                if (this.voicePipecatEnabled) {
                    this.hangupVoiceCall(true);
                    const msg = err?.data?.message || err?.message;
                    if (msg === 'voice_concurrency_limit') {
                        this.error = 'Túl sok egyidejű hanghívás. Próbáld újra pár másodperc múlva.';
                    } else if (
                        err?.status === 429
                        || (typeof msg === 'string' && /too many attempts/i.test(msg))
                    ) {
                        this.error = 'Túl sok hangindítás rövid időn belül. Várj kb. 1 percet, majd próbáld újra.';
                    } else {
                        this.error = msg
                            ? `Hang (Pipecat): ${msg}`
                            : 'Hangos beszélgetés indítása sikertelen.';
                    }

                    return;
                }
                // WS / token hiba → V1 fallback (csak ha nincs Pipecat)
            }

            await this.startVoiceListening();
        },

        async startVoicePipecatCall(micTrackPromise = null, greetingDone = null) {
            if (this.voicePipecatCall) {
                try {
                    await this.voicePipecatCall.stop();
                } catch {
                    // ignore
                }
                this.voicePipecatCall = null;
            }

            this.voiceState = 'connecting';
            // Ha van köszönő hang + szöveg: a buborék egyezzen a hallott bemutatkozással.
            // Audio nélkül ne mutassunk hamis köszönőt — az LLM első válasza hozza a disclosure-t.
            const greetingText = (this.voiceGreetingUrl && this.voiceGreetingText)
                ? String(this.voiceGreetingText).trim()
                : '';
            let showedVoiceGreeting = greetingText !== '';

            this.messages.push(showedVoiceGreeting
                ? {
                    role: 'assistant',
                    content: greetingText,
                    channel: 'voice',
                    voiceGreeting: true,
                }
                : {
                    role: 'assistant',
                    content: 'Hangos beszélgetés kapcsolódása…',
                    channel: 'voice',
                    voiceStatus: true,
                });
            this.stickToBottom = true;
            this.$nextTick(() => this.followScroll());

            // A session hívás és a mikrofon előmelegítés párhuzamosan fut.
            const sessionPromise = this.api.startVoicePipecatSession(this.conversationId);
            const micTrack = micTrackPromise ? await micTrackPromise.catch(() => null) : null;
            this.markVoiceTiming('mic_ready');

            let data;
            try {
                data = await sessionPromise;
            } catch (err) {
                micTrack?.stop();

                throw err;
            }
            this.markVoiceTiming('session');
            this.voicePipecatToken = data?.token ?? null;
            this.voicePipecatSessionOpen = Boolean(this.voicePipecatToken);
            if (data?.voice_greeting_text) {
                this.voiceGreetingText = data.voice_greeting_text;
            }

            // Configban gyakran még nincs MP3 (új fingerprint / queue nélkül) —
            // a session sync legyártja; ha eddig nem szólt a köszönés, most lejátszuk.
            const sessionGreetingUrl = data?.voice_greeting_audio_url || null;
            if (sessionGreetingUrl && ! this.voiceGreetingPlaying) {
                const alreadyHadAudio = Boolean(this.voiceGreetingAudio);
                if (! alreadyHadAudio) {
                    greetingDone = this.playVoiceGreeting(sessionGreetingUrl);
                    const text = String(this.voiceGreetingText || '').trim();
                    if (text !== '') {
                        const statusIdx = this.messages.findIndex((m) => m.voiceStatus);
                        if (statusIdx >= 0) {
                            this.messages[statusIdx] = {
                                role: 'assistant',
                                content: text,
                                channel: 'voice',
                                voiceGreeting: true,
                            };
                            showedVoiceGreeting = true;
                        }
                    }
                }
            }

            if (! this.voiceCallActive) {
                micTrack?.stop();
                this.releaseVoicePipecatSession();

                return;
            }
            if (! data?.daily_room || ! data?.daily_token) {
                micTrack?.stop();

                throw new Error('Pipecat session hiányos válasz');
            }

            let readyAnnounced = false;
            const callGen = (this._voicePipecatGen = (this._voicePipecatGen || 0) + 1);
            /** @type {number|null} */
            let pendingUserMsgIdx = null;
            /** @type {number|null} */
            let pendingBotMsgIdx = null;

            this.voicePipecatCall = createVoicePipecatSession({
                onTiming: (label) => this.markVoiceTiming(label),
                onState: (state) => {
                    if (callGen !== this._voicePipecatGen || (! this.voiceCallActive && state !== 'idle')) {
                        return;
                    }
                    // Piros (listening) csak line_open után — join/köszönés alatt connecting marad.
                    const previousState = this.voiceState;
                    if (state === 'listening' && ! this._voiceLineOpen) {
                        this.voiceState = 'connecting';
                    } else {
                        this.voiceState = state;
                    }
                    if (state === 'processing') {
                        this.showVoiceThinking();
                    } else if (state === 'idle' || (state === 'listening' && previousState === 'speaking')) {
                        this.clearVoiceThinking();
                    }
                    this.armVoiceStuckWatchdog();
                },
                onError: (message) => {
                    if (callGen !== this._voicePipecatGen) {
                        return;
                    }
                    if (message) {
                        this.error = `Hang: ${message}`;
                    }
                },
                onReady: () => {
                    if (callGen !== this._voicePipecatGen || readyAnnounced || ! this.voiceCallActive) {
                        return;
                    }
                    readyAnnounced = true;
                    this.messages = this.messages.filter((m) => ! m.voiceStatus);
                    // Köszönő buborék már bemutatkozott — ne írjunk rá felesleges „elindult” sort.
                    if (! showedVoiceGreeting) {
                        this.messages.push({
                            role: 'assistant',
                            content: 'Hangos beszélgetés elindult — beszélhetsz.',
                            channel: 'voice',
                        });
                    }
                    this.stickToBottom = true;
                    this.$nextTick(() => this.followScroll());
                },
                onUserTranscript: (text, final) => {
                    if (callGen !== this._voicePipecatGen || ! this.voiceCallActive || ! text) {
                        return;
                    }
                    const trimmed = String(text).trim();
                    if (! trimmed || trimmed === this.voiceBookingSignal) {
                        return;
                    }
                    this.markVoiceTiming(final ? 'user_transcript' : 'user_transcript_partial');
                    this.stopVoiceGreeting();
                    if (pendingUserMsgIdx !== null && this.messages[pendingUserMsgIdx]?.channel === 'voice') {
                        this.messages[pendingUserMsgIdx].content = trimmed;
                    } else {
                        pendingUserMsgIdx = this.messages.length;
                        this.messages.push({ role: 'user', content: trimmed, channel: 'voice' });
                    }
                    if (final) {
                        pendingUserMsgIdx = null;
                    }
                    this.stickToBottom = true;
                    this.$nextTick(() => this.followScroll());
                },
                onBotText: (text, final = true) => {
                    if (callGen !== this._voicePipecatGen || ! this.voiceCallActive || ! text) {
                        return;
                    }
                    this.stopVoiceGreeting();
                    const trimmed = String(text).trim();
                    if (! trimmed) {
                        return;
                    }
                    this.markVoiceTiming('bot_text');
                    const thinkingIdx = this.messages.findIndex((m) => m.voiceThinking);
                    if (pendingBotMsgIdx !== null && this.messages[pendingBotMsgIdx]?.channel === 'voice') {
                        this.messages[pendingBotMsgIdx].content = trimmed;
                    } else if (thinkingIdx >= 0) {
                        pendingBotMsgIdx = thinkingIdx;
                        this.messages[thinkingIdx] = { role: 'assistant', content: trimmed, channel: 'voice' };
                    } else {
                        pendingBotMsgIdx = this.messages.length;
                        this.messages.push({ role: 'assistant', content: trimmed, channel: 'voice' });
                    }
                    if (final) {
                        pendingBotMsgIdx = null;
                        // Pipecat app-message nélkül is: Laravel már pending_human lehet.
                        this.pollVoiceHandoverState();
                    }
                    this.stickToBottom = true;
                    this.$nextTick(() => this.followScroll());
                },
                onLeadForm: (event) => {
                    if (callGen !== this._voicePipecatGen || ! this.voiceCallActive || ! event) {
                        return;
                    }
                    this.pushLeadFormMessage(event);
                    this.pollVoiceHandoverState();
                },
                onHandoverStatus: (event) => {
                    // Ne kösd callGen-hez: a hívás már leállhatott, de az átvétel státuszát
                    // és a szöveges élő csatornát akkor is érvényesíteni kell.
                    if (! event?.status) {
                        return;
                    }
                    this.applyHandoverStatus(event.status, {
                        message: event.message
                            || (event.status === 'pending_human'
                                ? (this.config?.handover_pending_message ?? null)
                                : null),
                        botSilenced: event.bot_silenced === true,
                        endVoice: event.end_voice === true
                            || event.status === 'pending_human'
                            || event.status === 'human_active',
                        voiceToTextNotice: event.voice_to_text_notice
                            || this.config?.handover_voice_to_text_notice
                            || null,
                        fromVoice: true,
                    });
                },
                onEnded: () => {
                    if (callGen !== this._voicePipecatGen) {
                        return;
                    }
                    if (this.voiceCallActive) {
                        this.hangupVoiceCall(true);
                    }
                },
            });

            try {
                await this.voicePipecatCall.start({
                    dailyRoom: data.daily_room,
                    dailyToken: data.daily_token,
                    audioTrack: micTrack,
                    // A köszönés alatt zárva a vonal: a hangszóró hangja ne induljon fordulót.
                    startMuted: this.voiceGreetingPlaying,
                });

                const openLine = () => {
                    if (! this.voiceCallActive || callGen !== this._voicePipecatGen) {
                        return;
                    }
                    this._voiceLineOpen = true;
                    this.voicePipecatCall?.unmute();
                    this.voiceState = 'listening';
                    this.markVoiceTiming('line_open');
                };

                if (greetingDone) {
                    greetingDone.then(openLine);
                } else {
                    openLine();
                }
            } catch (err) {
                try {
                    await this.voicePipecatCall?.stop();
                } catch {
                    // ignore
                }
                this.voicePipecatCall = null;
                throw err;
            }
        },

        async startVoiceWsCall(wsUrl) {
            this.voiceWsCall?.stop();
            this.voiceWsCall = createVoiceWsSession({
                wsUrl,
                getConversationId: () => this.conversationId,
                getVoiceToken: async () => {
                    const s = await this.voiceSession?.ensureToken();

                    return s?.token ?? null;
                },
                onTranscript: (text) => {
                    this.messages.push({ role: 'user', content: text, channel: 'voice' });
                    this.stickToBottom = true;
                    this.$nextTick(() => this.followScroll());
                },
                onReply: (text) => {
                    this.messages.push({ role: 'assistant', content: text, channel: 'voice' });
                    this.stickToBottom = true;
                    this.$nextTick(() => this.followScroll());
                },
                onVoiceDisabled: () => this.disableVoiceUi(),
                onError: (message) => {
                    if (message && message !== 'empty_transcript' && message !== 'empty_audio') {
                        this.error = `Hang: ${message}`;
                    }
                },
                onState: (state) => {
                    this.voiceState = state;
                },
            });

            try {
                await this.voiceWsCall.start();
            } catch (err) {
                this.voiceWsCall = null;
                this.error = err?.message || 'Voice szolgáltatás nem elérhető — V1 mód.';
                await this.startVoiceListening();
            }
        },

        async startVoiceListening() {
            if (! this.voiceCallActive || ! this.voiceEnabled) {
                return;
            }

            if (! window.isSecureContext || ! navigator.mediaDevices?.getUserMedia) {
                this.error = 'A hanghoz https kell (vagy localhost). A http://chatbot.test címen a böngésző nem kér mikrofont.';
                this.hangupVoiceCall(true);

                return;
            }

            try {
                this.error = null;
                await this.unlockAudioForIos();
                this.voiceSkipProcess = false;
                this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                this.voiceChunks = [];
                this.voiceStartedAt = Date.now();
                const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                    ? 'audio/webm;codecs=opus'
                    : '';
                this.mediaRecorder = new MediaRecorder(
                    this.mediaStream,
                    mime ? { mimeType: mime } : undefined,
                );
                this.mediaRecorder.ondataavailable = (e) => {
                    if (e.data && e.data.size > 0) {
                        this.voiceChunks.push(e.data);
                    }
                };
                this.mediaRecorder.onstop = () => {
                    if (this.voiceSkipProcess) {
                        this.voiceSkipProcess = false;
                        this.voiceChunks = [];

                        return;
                    }
                    this.processVoiceRecording();
                };
                this.mediaRecorder.start(250);
                this.voiceState = 'listening';

                this.stopSilenceDetector();
                this.silenceDetector = createSilenceDetector({
                    stream: this.mediaStream,
                    onSilence: () => {
                        if (this.voiceCallActive && this.voiceState === 'listening') {
                            this.finishVoiceRecording();
                        }
                    },
                });

                this.voiceSession?.ensureToken().catch((err) => {
                    if (this.isVoicePlanUnavailable(err)) {
                        this.disableVoiceUi();
                    }
                });
            } catch (err) {
                this.hangupVoiceCall(true);

                const name = err?.name ?? '';
                if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
                    this.error = 'Engedélyezd a mikrofont a böngészőben (címsáv → mikrofon ikon), majd próbáld újra.';
                } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
                    this.error = 'Nem található mikrofon ezen a gépen.';
                } else {
                    this.error = 'A mikrofon most nem indítható. Próbáld https-sel vagy localhoston.';
                }
            }
        },

        finishVoiceRecording() {
            this.stopSilenceDetector();
            if (! this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
                if (this.voiceCallActive) {
                    this.voiceState = 'idle';
                }

                return;
            }
            this.voiceState = 'processing';
            try {
                this.mediaRecorder.stop();
            } catch {
                // ignore
            }
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach((t) => t.stop());
                this.mediaStream = null;
            }
        },

        async playVoiceReply(text) {
            if (! text || ! this.voiceCallActive) {
                return;
            }

            this.voiceState = 'speaking';
            const blob = await this.api.speakVoice(text);
            if (! this.voiceCallActive) {
                return;
            }

            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            this.voicePlayback = audio;

            await new Promise((resolve, reject) => {
                audio.onended = () => resolve();
                audio.onerror = () => reject(new Error('Hang lejátszás sikertelen.'));
                audio.play().catch(reject);
            }).finally(() => {
                URL.revokeObjectURL(url);
                if (this.voicePlayback === audio) {
                    this.voicePlayback = null;
                }
            });
        },

        async processVoiceRecording() {
            const blob = new Blob(this.voiceChunks, {
                type: 'audio/webm',
            });
            this.voiceChunks = [];
            this.mediaRecorder = null;

            if (! this.voiceCallActive) {
                this.voiceState = 'idle';

                return;
            }

            if (! blob.size) {
                this.error = 'Nem hallottam semmit — beszélj, a csend után küldöm.';
                if (this.voiceCallActive) {
                    await this.startVoiceListening();
                }

                return;
            }

            this.streaming = true;
            this.voiceState = 'processing';
            this.error = null;

            const statusIndex = this.messages.length;
            this.messages.push({
                role: 'assistant',
                content: 'Szöveggé alakítom a hangod…',
                typing: true,
                voiceStatus: true,
            });
            this.stickToBottom = true;
            this.$nextTick(() => this.followScroll());

            try {
                if (! this.conversationId) {
                    await this.initConversation();
                }

                if (! this.voiceCallActive) {
                    return;
                }

                const elapsedSeconds = Math.max(1, Math.round((Date.now() - this.voiceStartedAt) / 1000));

                const stt = await this.api.transcribeVoiceAudio(blob);
                if (! this.voiceCallActive) {
                    return;
                }

                if (stt.voice_enabled === false) {
                    this.messages.splice(statusIndex, 1);
                    this.disableVoiceUi();

                    return;
                }

                this.messages.splice(statusIndex, 1);
                this.messages.push({
                    role: 'user',
                    content: stt.transcript,
                    channel: 'voice',
                });
                this.stickToBottom = true;
                this.$nextTick(() => this.followScroll());

                const typingIndex = this.messages.length;
                this.messages.push({ role: 'assistant', content: '', typing: true });
                this.$nextTick(() => this.followScroll());

                const session = await this.voiceSession?.ensureToken();
                if (! this.voiceCallActive) {
                    return;
                }

                const result = await this.api.sendVoiceTurn(
                    this.conversationId,
                    stt.transcript,
                    session?.token ?? null,
                    elapsedSeconds,
                );

                if (! this.voiceCallActive) {
                    return;
                }

                const reply = result.reply || '';
                this.patchMessage(typingIndex, {
                    content: reply,
                    typing: false,
                    channel: 'voice',
                    id: result.message_id ?? null,
                });

                if (result.voice_enabled === false) {
                    this.disableVoiceUi();

                    return;
                }

                this.stickToBottom = true;
                this.$nextTick(() => this.followScroll());

                this.streaming = false;

                if (reply) {
                    try {
                        await this.playVoiceReply(reply);
                    } catch (playErr) {
                        if (this.isVoicePlanUnavailable(playErr)) {
                            this.disableVoiceUi();

                            return;
                        }
                        // Szöveg megvan; hang nélkül folytatjuk a kört
                        this.error = playErr?.data?.message || playErr?.message
                            ? `Hangos válasz sikertelen: ${playErr?.data?.message || playErr.message}`
                            : null;
                    }
                }

                if (this.voiceCallActive && this.voiceEnabled) {
                    await this.startVoiceListening();
                } else {
                    this.voiceState = 'idle';
                }
            } catch (err) {
                this.messages = this.messages.filter((m) => ! m.voiceStatus && ! (m.typing && ! m.content));

                if (this.isVoicePlanUnavailable(err)) {
                    this.disableVoiceUi();
                } else {
                    const detail = err?.data?.message || err?.message;
                    this.error = detail
                        ? `Hangüzenet sikertelen: ${detail}`
                        : 'Hangüzenet sikertelen. Próbáld újra.';
                    if (this.voiceCallActive) {
                        await this.startVoiceListening();
                    } else {
                        this.voiceState = 'idle';
                    }
                }
            } finally {
                this.streaming = false;
            }
        },

        async restoreConversationFromStorage() {
            const sessionId = getOrCreateSessionId();
            const stored = getStoredConversationId(this.botKey);

            if (! stored) {
                this.beginNewSession();

                return;
            }

            try {
                const data = await this.api.fetchMessageHistory(stored, sessionId);
                const history = data.messages ?? [];

                if (history.length === 0) {
                    // Stale conv id vagy üres thread — ne örököljük a szerver commerce_state-et.
                    clearStoredConversationId(this.botKey);
                    resetWidgetSession();
                    this.conversationId = null;
                    this.beginNewSession();

                    return;
                }

                this.deactivateAiDisclosure();
                this.conversationId = data.conversation_id;
                setStoredConversationId(this.botKey, this.conversationId);

                for (const message of history) {
                    if (message.role === 'agent' && message.metadata?.lead_form) {
                        const leadEvent = leadFormEventFromMetadata(
                            message.metadata.lead_form,
                            message.id,
                        );
                        if (leadEvent) {
                            this.pushLeadFormMessage(leadEvent);
                        } else {
                            this.messages.push(mapHistoryMessage(message));
                        }
                    } else {
                        this.messages.push(mapHistoryMessage(message));
                    }
                    if (message.metadata?.handover_closed) {
                        this.closeNoticeShown = true;
                    }
                }

                try {
                    const ensured = await this.api.ensureConversation(sessionId, this.conversationId);
                    this.broadcastAuthToken = ensured.broadcast_auth_token ?? null;
                    this.reverbConfig = ensured.reverb ?? null;

                    if (ensured.handover?.handover_status) {
                        this.applyHandoverStatus(ensured.handover.handover_status, {
                            agentName: ensured.handover.agent_name ?? null,
                        });
                    }

                    this.subscribeHandoverChannel();
                } catch {
                    await this.syncHandoverState();
                }

                this.stickToBottom = true;
                this.$nextTick(() => this.followScroll());
            } catch {
                clearStoredConversationId(this.botKey);
                resetWidgetSession();
                this.conversationId = null;
                this.beginNewSession();
            }
        },

        beginNewSession() {
            this.activateAiDisclosure();
            this.showWelcomeMessage();
        },

        activateAiDisclosure() {
            this.showAiDisclosure = Boolean(this.config?.ai_disclosure);
        },

        deactivateAiDisclosure() {
            this.showAiDisclosure = false;
        },

        showWelcomeMessage() {
            if (! this.config?.welcome_message) {
                return;
            }

            this.messages.push({
                role: 'assistant',
                content: this.config.welcome_message,
            });
        },

        showVoiceThinking() {
            if (this.messages.some((m) => m.voiceThinking)) {
                return;
            }
            this.messages.push({ role: 'assistant', content: '', typing: true, channel: 'voice', voiceThinking: true });
            this.stickToBottom = true;
            this.$nextTick(() => this.followScroll());
        },

        clearVoiceThinking() {
            const last = this.messages[this.messages.length - 1];
            if (last?.voiceThinking) {
                this.messages.pop();
            }
        },

        startNewConversation() {
            // Az élő hanghívás a régi beszélgetéshez kötődik — új chatnél le kell tenni.
            if (this.voiceCallActive || this.voicePipecatCall || this.voiceWsCall) {
                this.hangupVoiceCall(true);
            }

            if (this.streaming) {
                return;
            }

            clearStoredConversationId(this.botKey);
            resetWidgetSession();
            this.conversationId = null;
            this.messages = [];
            this.error = null;
            this.input = '';
            this.beginNewSession();
            this.stickToBottom = true;
            this.$nextTick(() => this.followScroll());
        },

        toggle() {
            this.open = ! this.open;
            this.error = null;
            this.upgradeUrl = null;

            if (this.open) {
                this.proactiveVisible = false;
                this.stopProactiveInvitation();
                this.stickToBottom = true;
                this.followScroll();
                this.focusInput();
                // A Daily SDK és a köszönés töltése itt induljon, ne a mikrofon kattintáskor.
                if (this.voicePipecatEnabled) {
                    preloadVoicePipecat();
                    this.preloadVoiceGreeting();
                }
            }
        },

        async send() {
            const text = this.input.trim();

            if (! text || this.streaming) {
                return;
            }

            this.error = null;
            this.upgradeUrl = null;
            this.input = '';
            this.stickToBottom = true;
            this.messages.push({ role: 'user', content: text });
            this.followScroll();
            this.streaming = true;

            const typingIndex = this.messages.length;
            this.messages.push({ role: 'assistant', content: '', typing: true });
            this.followScroll();

            try {
                if (! this.conversationId) {
                    await this.initConversation();
                }

                const adminSession = this.adminMcpSession
                    ? await this.adminMcpSession.getSession()
                    : null;

                const response = this.adminMcpSession
                    ? await this.api.streamAdminMessage(this.conversationId, text, adminSession)
                    : await this.api.streamMessage(this.conversationId, text);

                if (response.status === 429) {
                    const data = await response.json().catch(() => ({}));
                    this.upgradeUrl = data.upgrade_url ?? null;
                    throw new Error(data.message ?? 'Elérted az üzenetlimitet.');
                }

                if (! response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(data.message ?? `Hiba: ${response.status}`);
                }

                await consumeChatStream(
                    response,
                    (full) => {
                        this.patchMessage(typingIndex, {
                            role: 'assistant',
                            content: full,
                            typing: true,
                        });
                        this.followScroll();
                    },
                    async (full) => {
                        const current = this.messages[typingIndex];

                        if (current?.typing && ! full && this.handoverStatus !== 'bot_active') {
                            this.messages.splice(typingIndex, 1);

                            return;
                        }

                        this.patchMessage(typingIndex, {
                            role: 'assistant',
                            content: full || '…',
                            typing: false,
                        });
                        await this.resolveAssistantMessageId(typingIndex);
                        this.followScroll();
                    },
                    (persisted) => {
                        const patch = {};

                        if (persisted?.assistantMessageId != null) {
                            patch.id = persisted.assistantMessageId;
                        }

                        if (persisted?.sourceAttribution) {
                            patch.sourceAttribution = persisted.sourceAttribution;
                        }

                        if (persisted?.commerceMedia) {
                            patch.commerceMedia = persisted.commerceMedia;
                        }

                        if (Object.keys(patch).length > 0) {
                            this.patchMessage(typingIndex, patch);
                        }

                        if (persisted?.commerceMedia) {
                            this.$nextTick(() => this.followScroll());
                        }
                    },
                    (content) => {
                        this.patchMessage(typingIndex, {
                            role: 'assistant',
                            content,
                            typing: true,
                        });
                        this.followScroll();
                    },
                    (leadEvent) => {
                        this.pushLeadFormMessage(leadEvent);
                    },
                    (toolEvent) => {
                        const summary = toolEvent.summary ?? toolEvent.tool_name ?? 'művelet';

                        this.patchMessage(typingIndex, {
                            toolActivity: `🔧 Művelet folyamatban: ${summary}`,
                        });
                        this.followScroll();
                    },
                    () => {
                        this.patchMessage(typingIndex, { toolActivity: null });
                    },
                    (handoverEvent) => {
                        this.applyHandoverStatus(handoverEvent.status, {
                            agentName: handoverEvent.agent_name ?? null,
                            message: handoverEvent.message ?? null,
                            botSilenced: handoverEvent.bot_silenced === true,
                        });

                        if (handoverEvent.bot_silenced && this.messages[typingIndex]?.typing) {
                            this.messages.splice(typingIndex, 1);
                        }
                    },
                );
            } catch (err) {
                const current = this.messages[typingIndex];
                const hasContent = typeof current?.content === 'string' && current.content.trim() !== '';

                if (hasContent) {
                    this.patchMessage(typingIndex, {
                        typing: false,
                        toolActivity: null,
                    });
                } else {
                    this.messages.splice(typingIndex, 1);
                }

                this.error = err.message ?? 'Hiba történt a küldés során.';
            } finally {
                this.streaming = false;
                this.followScroll();
                this.focusInput();
            }
        },

        async initConversation() {
            const sessionId = getOrCreateSessionId();
            const stored = getStoredConversationId(this.botKey);
            const data = await this.api.ensureConversation(sessionId, stored);
            this.conversationId = data.conversation_id;
            this.broadcastAuthToken = data.broadcast_auth_token ?? null;
            this.reverbConfig = data.reverb ?? null;
            setStoredConversationId(this.botKey, this.conversationId);

            if (data.handover?.handover_status) {
                this.applyHandoverStatus(data.handover.handover_status, {
                    agentName: data.handover.agent_name ?? null,
                });
            }

            this.subscribeHandoverChannel();
        },

        applyHandoverStatus(status, extras = {}) {
            const previousStatus = this.handoverStatus;
            this.handoverStatus = status || 'bot_active';
            this.handoverAgentName = extras.agentName ?? this.handoverAgentName;

            if (extras.message) {
                this.handoverBanner = extras.message;
            } else {
                this.handoverBanner = handoverBannerText(this.handoverStatus, this.handoverAgentName);
            }

            const isLive = this.handoverStatus === 'pending_human'
                || this.handoverStatus === 'human_active';
            const enteredLive = isLive && previousStatus !== this.handoverStatus;
            const voiceStillUp = this.voiceCallActive
                || !! this.voicePipecatCall
                || !! this.voiceWsCall;

            const shouldEndVoice = extras.endVoice === true
                || (isLive && (extras.fromVoice === true || voiceStillUp));

            if (shouldEndVoice && voiceStillUp) {
                this.hangupVoiceCall(true);
            }

            if (isLive) {
                // Hang → szöveg: Echo + poll (Pipecat app-message nélkül is).
                if (enteredLive || extras.fromVoice === true || extras.forceRealtime === true) {
                    this.ensureHandoverRealtime();
                    this.startHandoverPolling({ immediate: true });
                    void this.syncHandoverMessages();
                    this.open = true;
                    this.$nextTick(() => {
                        this.focusInput();
                        this.followScroll();
                    });

                    const notice = extras.voiceToTextNotice
                        || (extras.fromVoice
                            ? (this.config?.handover_voice_to_text_notice ?? null)
                            : null);
                    if (notice) {
                        this.appendAssistantNoticeIfNew({
                            id: `voice-to-text-${this.conversationId || 'local'}`,
                            content: notice,
                        });
                    }
                } else {
                    // Már élő módban — timer biztosítása, üzenetek a poll/Echo útján jönnek.
                    this.startHandoverPolling({ immediate: false });
                }
            } else if (this.handoverStatus === 'closed' && ! this.closeNoticeShown) {
                // Reverb nélkül a következő poll hozza a close_notice-t
                this.startHandoverPolling({ immediate: true });
            } else if (! this.voiceCallActive) {
                // Hanghívás közben ne állítsuk le a szöveges pollt csak azért, mert
                // egy köztes sync még bot_active-ot látott.
                this.stopHandoverPolling();
            }
        },

        /**
         * Hanghívás alatt: Laravel átvétel státusz figyelése (Pipecat deploy nélkül is).
         */
        startVoiceHandoverWatch() {
            this.stopVoiceHandoverWatch();
            this.voiceHandoverPollTimer = setInterval(() => {
                this.pollVoiceHandoverState();
            }, 2000);
            this.pollVoiceHandoverState();
        },

        stopVoiceHandoverWatch() {
            if (this.voiceHandoverPollTimer) {
                clearInterval(this.voiceHandoverPollTimer);
                this.voiceHandoverPollTimer = null;
            }
        },

        async pollVoiceHandoverState() {
            if (! this.voiceCallActive || ! this.conversationId || ! this.api) {
                return;
            }

            try {
                const state = await this.api.fetchHandoverState(this.conversationId);
                const status = state.handover_status;

                if (status !== 'pending_human' && status !== 'human_active') {
                    return;
                }

                this.applyHandoverStatus(status, {
                    agentName: state.agent_name ?? null,
                    message: status === 'pending_human'
                        ? (this.config?.handover_pending_message ?? null)
                        : null,
                    fromVoice: true,
                    endVoice: true,
                    voiceToTextNotice: this.config?.handover_voice_to_text_notice ?? null,
                });
            } catch {
                // best-effort
            }
        },

        /**
         * Élő átvétel után a szöveges Reverb / poll út kötelező — hanghívás közben
         * is lehet, hogy a csatorna nem volt aktív vagy a poll leállt.
         */
        ensureHandoverRealtime() {
            if (! this.conversationId) {
                return;
            }

            if (this.reverbConfig && this.broadcastAuthToken) {
                this.subscribeHandoverChannel();
            }

            void this.syncHandoverMessages();
        },

        startHandoverPolling({ immediate = true } = {}) {
            const wasIdle = ! this.handoverPollTimer;
            if (wasIdle) {
                // Élő inbox: 2 mp-es poll, ha Echo/Reverb nem jön át
                this.handoverPollTimer = setInterval(() => {
                    this.syncHandoverState();
                }, 2000);
            }

            // Azonnali sync: új timer indításakor, vagy ha explicit kérjük — de ne
            // rekurzívan syncHandoverState → apply → startPolling → sync körben.
            if ((wasIdle || immediate) && ! this._handoverSyncInFlight) {
                void this.syncHandoverState();
            }
        },

        stopHandoverPolling() {
            if (this.handoverPollTimer) {
                clearInterval(this.handoverPollTimer);
                this.handoverPollTimer = null;
            }
        },

        appendAgentMessageIfNew(payload) {
            const id = payload.id ?? payload.message_id ?? null;
            const content = payload.content ?? '';

            if (id != null && this.messages.some((m) => m.id === id)) {
                return false;
            }

            if (id == null && this.messages.some((m) => m.role === 'agent' && m.content === content)) {
                return false;
            }

            this.messages.push({
                role: 'agent',
                content,
                id,
                agentName: payload.agentName ?? payload.agent_name ?? 'Ügyfélszolgálat',
            });

            return true;
        },

        appendAssistantNoticeIfNew(payload) {
            const id = payload.id ?? null;
            const content = payload.content ?? '';

            if (! content) {
                return false;
            }

            if (id != null && this.messages.some((m) => m.id === id)) {
                this.closeNoticeShown = true;
                if (this.handoverStatus === 'closed') {
                    this.stopHandoverPolling();
                }

                return false;
            }

            if (id == null && this.messages.some((m) => m.role === 'assistant' && m.content === content)) {
                this.closeNoticeShown = true;
                if (this.handoverStatus === 'closed') {
                    this.stopHandoverPolling();
                }

                return false;
            }

            this.messages.push({
                role: 'assistant',
                content,
                id,
            });

            this.closeNoticeShown = true;
            if (this.handoverStatus === 'closed') {
                this.stopHandoverPolling();
            }

            return true;
        },

        subscribeHandoverChannel() {
            this.handoverSubscription?.leave?.();
            this.handoverSubscription = subscribeConversationChannel({
                reverb: this.reverbConfig,
                broadcastAuthToken: this.broadcastAuthToken,
                botKey: this.botKey,
                conversationId: this.conversationId,
                onStatusChanged: (event) => {
                    this.applyHandoverStatus(event.status, {
                        agentName: event.agent_name ?? null,
                    });

                    const noticeContent = event.notice ?? event.message ?? null;
                    const noticeId = event.notice_message_id ?? event.message_id ?? null;

                    if (noticeContent) {
                        this.appendAssistantNoticeIfNew({
                            id: noticeId,
                            content: noticeContent,
                        });
                        this.followScroll();
                    }

                    this.syncHandoverMessages();

                    if (event.status === 'closed') {
                        this.syncClosedNoticeMessages();
                    }
                },
                onAgentMessage: (event) => {
                    this.applyHandoverStatus('human_active', {
                        agentName: event.agent_name ?? null,
                    });

                    const leadEvent = leadFormEventFromAgentEvent(event);
                    if (leadEvent) {
                        this.pushLeadFormMessage(leadEvent);
                        this.followScroll();

                        return;
                    }

                    if (this.appendAgentMessageIfNew({
                        id: event.message_id ?? null,
                        content: event.content ?? '',
                        agent_name: event.agent_name ?? 'Ügyfélszolgálat',
                    })) {
                        this.followScroll();
                    }
                },
            });

            // WebSocket mellett biztonsági háló: poll, ha Echo/Reverb nem jön át (HTTPS/ws, auth, stb.)
            this.startHandoverPolling();
        },

        async syncHandoverState() {
            if (! this.conversationId || ! this.api || this._handoverSyncInFlight) {
                return;
            }

            this._handoverSyncInFlight = true;

            try {
                const state = await this.api.fetchHandoverState(this.conversationId);
                this.applyHandoverStatus(state.handover_status, {
                    agentName: state.agent_name ?? null,
                });
                await this.syncHandoverMessages();

                if (state.close_notice?.content) {
                    if (this.appendAssistantNoticeIfNew({
                        id: state.close_notice.id ?? null,
                        content: state.close_notice.content,
                    })) {
                        this.followScroll();
                    }
                } else if (state.handover_status === 'closed') {
                    await this.syncClosedNoticeMessages();
                }
            } catch {
                // reconnect sync best-effort
            } finally {
                this._handoverSyncInFlight = false;
            }
        },

        async syncHandoverMessages() {
            if (! this.conversationId || ! this.api) {
                return;
            }

            if (this.handoverStatus !== 'pending_human' && this.handoverStatus !== 'human_active') {
                return;
            }

            try {
                const sessionId = getOrCreateSessionId();
                const data = await this.api.fetchMessageHistory(this.conversationId, sessionId);
                let added = false;

                for (const message of data.messages ?? []) {
                    if (message.role !== 'agent') {
                        continue;
                    }

                    const leadEvent = leadFormEventFromMetadata(
                        message.metadata?.lead_form,
                        message.id,
                    );

                    if (leadEvent) {
                        if (this.pushLeadFormMessage(leadEvent)) {
                            added = true;
                        }

                        continue;
                    }

                    if (this.appendAgentMessageIfNew({
                        id: message.id,
                        content: message.content,
                        agent_name: message.metadata?.agent_name ?? 'Ügyfélszolgálat',
                    })) {
                        added = true;
                    }
                }

                if (added) {
                    this.followScroll();
                }
            } catch {
                // best-effort
            }
        },

        async syncClosedNoticeMessages() {
            if (! this.conversationId || ! this.api) {
                return;
            }

            try {
                const sessionId = getOrCreateSessionId();
                const data = await this.api.fetchMessageHistory(this.conversationId, sessionId);
                let added = false;

                for (const message of data.messages ?? []) {
                    if (message.role !== 'assistant' || ! message.metadata?.handover_closed) {
                        continue;
                    }

                    if (this.appendAssistantNoticeIfNew(message)) {
                        added = true;
                    }
                }

                if (added) {
                    this.followScroll();
                }
            } catch {
                // best-effort
            }
        },

        pushLeadFormMessage(event) {
            // Ugyanaz az űrlap újra megjelenhet: a korábbi kártya (akár beküldött) marad,
            // az új szándékhoz friss, előkitöltött kártya kerül a beszélgetés aljára.
            // Agent history / poll: source_message_id alapján ne duplikáljuk ugyanazt a kártyát.
            const id = event?.source_message_id ?? null;
            if (id != null && this.messages.some((m) => m.role === 'lead_form' && m.id === id)) {
                return false;
            }

            this.messages.push(createLeadFormMessage(event));
            this.followScroll();

            return true;
        },

        canSubmitLeadForm(msg) {
            return canSubmitLeadForm(msg);
        },

        async submitLeadFormMessage(msg) {
            if (! this.canSubmitLeadForm(msg) || msg.state === 'submitting') {
                return;
            }

            if (! this.conversationId) {
                await this.initConversation();
            }

            msg.state = 'submitting';
            msg.errorMessage = null;

            try {
                const result = msg.payload.kind === 'booking'
                    ? await this.api.submitBookingForm(this.conversationId, msg.fieldValues)
                    : await this.api.submitLeadForm(
                        msg.formId,
                        this.conversationId,
                        msg.fieldValues,
                        msg.consentGiven,
                        msg.payload.gdpr_text ?? '',
                    );

                msg.state = 'success';
                msg.successMessage = result.message ?? msg.payload.success_message ?? 'Köszönjük!';
                msg.successUrl = result.url ?? null;
                msg.successUrlLabel = result.url_label ?? null;
                // Hívás közben a bot a saját hangján igazolja vissza a foglalást.
                if (msg.payload.kind === 'booking' && result.voice_confirm_signal && this.voicePipecatCall) {
                    this.voiceBookingSignal = result.voice_confirm_signal;
                    if (this.voicePipecatCall.sendText(result.voice_confirm_signal)) {
                        this.showVoiceThinking();
                        // Ha a hang agent nem indít fordulót, ne maradjon örökké a jelzés.
                        setTimeout(() => this.clearVoiceThinking(), 15000);
                    }
                }
            } catch (err) {
                msg.state = 'error';
                msg.errorMessage = err.message ?? 'Hiba történt a küldés során.';
            }

            this.followScroll();
        },

        dismissLeadFormMessage(msg) {
            const index = this.messages.indexOf(msg);

            if (index >= 0) {
                this.messages.splice(index, 1);
            }
        },

        isNearBottom() {
            const el = this.$refs.messages;

            if (! el) {
                return true;
            }

            const threshold = 96;

            return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
        },

        scrollToBottom() {
            const el = this.$refs.messages;

            if (el) {
                el.scrollTop = el.scrollHeight;
            }
        },

        focusInput() {
            if (! this.open || this.streaming) {
                return;
            }

            this.$nextTick(() => {
                this.$refs.input?.focus();
            });
        },

        onMessagesScroll() {
            this.stickToBottom = this.isNearBottom();
        },

        /** Stream és új üzenet közben követi az aljat; ha a user felfelé görgetett, nem rángatja. */
        followScroll() {
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    if (this.stickToBottom || this.isNearBottom()) {
                        this.scrollToBottom();
                    }
                });
            });
        },

        onKeydown(event) {
            if (event.key === 'Enter' && ! event.shiftKey) {
                event.preventDefault();
                this.send();
            }
        },

        formatAssistantMessage(msg) {
            return renderAssistantMessageHtml(msg?.content ?? '');
        },

        linkTarget(href) {
            return resolveLinkTarget(href, window.location.origin).target;
        },

        linkRel(href) {
            return resolveLinkTarget(href, window.location.origin).rel ?? undefined;
        },

        hasCommerceMedia(msg) {
            return hasCommerceMedia(msg);
        },

        patchMessage(index, patch) {
            const current = this.messages[index];

            if (! current) {
                return;
            }

            this.messages.splice(index, 1, { ...current, ...patch });
        },

        hasSubmittedFeedback(msg) {
            return msg.feedback === 'positive' || msg.feedback === 'negative';
        },

        canShowFeedback(msg) {
            return msg.role === 'assistant'
                && msg.id != null
                && ! msg.typing
                && (msg.content?.length ?? 0) > 0;
        },

        async resolveAssistantMessageId(messageIndex) {
            const msg = this.messages[messageIndex];

            if (! msg || msg.role !== 'assistant' || ! this.conversationId) {
                return;
            }

            if (msg.id != null && msg.sourceAttribution) {
                return;
            }

            try {
                const sessionId = getOrCreateSessionId();
                const data = await this.api.fetchMessageHistory(this.conversationId, sessionId);
                const assistants = (data.messages ?? []).filter((m) => m.role === 'assistant');
                const match = msg.id != null
                    ? assistants.find((m) => m.id === msg.id)
                    : null;
                const resolved = match ?? assistants[assistants.length - 1];

                if (resolved?.id == null) {
                    return;
                }

                this.patchMessage(messageIndex, {
                    id: resolved.id,
                    feedback: resolved.feedback ?? msg.feedback ?? null,
                    sourceAttribution: resolved.source_attribution?.text ?? msg.sourceAttribution ?? null,
                    commerceMedia: mapCommerceMedia(resolved.commerce_media) ?? msg.commerceMedia ?? null,
                });
            } catch {
                // A thumb fallback nélkül is megjelenhet, ha turn_persisted megérkezett.
            }
        },

        async submitFeedback(msg, satisfaction) {
            const index = this.messages.findIndex((candidate) => candidate.id === msg.id && candidate.id != null);

            if (index < 0 || ! this.canShowFeedback(msg) || this.hasSubmittedFeedback(msg) || msg.feedbackSubmitting) {
                return;
            }

            if (! this.conversationId) {
                return;
            }

            this.patchMessage(index, { feedbackSubmitting: true });

            try {
                const sessionId = getOrCreateSessionId();
                const data = await this.api.submitMessageFeedback(
                    this.conversationId,
                    msg.id,
                    sessionId,
                    satisfaction,
                );
                this.patchMessage(index, {
                    feedback: data.feedback ?? satisfaction,
                    feedbackSubmitting: false,
                });
            } catch {
                this.patchMessage(index, { feedbackSubmitting: false });
                this.error = 'A visszajelzés mentése nem sikerült.';
            }
        },
    }));
}

export function chatWidgetTemplate() {
    return `
    <div
        class="cb-widget-root"
        :class="{ 'cb-theme--dark': darkMode }"
        :style="{ '--cb-primary': primaryColor, '--cb-radius': borderRadius }"
    >
        <div
            x-show="proactiveVisible && !open"
            x-transition
            x-cloak
            class="cb-teaser"
            :class="positionClass === 'left' ? 'cb-teaser--left' : 'cb-teaser--right'"
            role="status"
            style="display: none;"
        >
            <button
                type="button"
                class="cb-teaser__text"
                @click="openFromProactive()"
                x-text="proactiveMessage"
            ></button>
            <button
                type="button"
                class="cb-teaser__close"
                @click="dismissProactive()"
                aria-label="Felkérés bezárása"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                    <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
            </button>
        </div>

        <button
            type="button"
            class="cb-bubble"
            :class="[
                positionClass === 'left' ? 'cb-bubble--left' : 'cb-bubble--right',
                avatarUrl ? 'cb-bubble--avatar' : '',
            ]"
            @click="toggle()"
            aria-label="Chat megnyitása"
        >
            <template x-if="avatarUrl">
                <img class="cb-bubble__avatar" :src="avatarUrl" alt="" />
            </template>
            <template x-if="!avatarUrl">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
            </template>
        </button>

        <div
            x-show="open"
            x-transition
            class="cb-panel"
            :class="positionClass === 'left' ? 'cb-panel--left' : 'cb-panel--right'"
            style="display: none;"
        >
            <div class="cb-panel__header">
                <span class="cb-panel__title" x-text="config?.name ?? 'Chat'"></span>
                <div class="cb-panel__header-actions">
                    <button
                        type="button"
                        class="cb-header-btn cb-header-btn--new"
                        x-show="hasActiveConversation"
                        x-cloak
                        @click="startNewConversation()"
                        :disabled="streaming"
                        aria-label="Új beszélgetés"
                        title="Új beszélgetés - a korábbi üzenetek nem jelennek meg itt"
                    >
                        <svg class="cb-header-btn__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                            <path d="M12 5v14M5 12h14"/>
                        </svg>
                        <span class="cb-header-btn__label">Új</span>
                    </button>
                    <button
                        type="button"
                        class="cb-header-btn"
                        @click="open = false"
                        aria-label="Panel bezárása"
                        title="Bezárás"
                    >
                        <svg class="cb-header-btn__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                            <path d="M18 6 6 18M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
            </div>

            <div
                class="cb-status-strip"
                :class="{
                    'cb-ai-disclosure': statusStripKind === 'ai',
                    'cb-handover-banner': statusStripKind === 'handover',
                }"
                x-show="statusStripText"
                x-cloak
                :role="statusStripKind === 'handover' ? 'status' : 'note'"
                x-text="statusStripText"
            ></div>

            <div class="cb-messages" x-ref="messages" @scroll.passive="onMessagesScroll()">
                <div class="cb-messages__inner">
                    <template x-for="(msg, index) in messages" :key="msg.id ? ('m-' + msg.id) : ('i-' + index)">
                        <div
                            class="cb-msg"
                            :class="{
                                'cb-msg--user': msg.role === 'user',
                                'cb-msg--assistant': msg.role === 'assistant' || msg.role === 'agent',
                                'cb-msg--agent': msg.role === 'agent',
                                'cb-msg--lead-form': msg.role === 'lead_form',
                                'cb-msg--typing': msg.typing
                            }"
                        >
                            <template x-if="msg.role === 'user'">
                                <span class="cb-msg__text" x-text="msg.content"></span>
                            </template>
                            <template x-if="msg.role === 'agent'">
                                <div class="cb-msg__assistant-wrap">
                                    <div class="cb-msg__agent-label" x-text="msg.agentName || 'Ügyfélszolgálat'"></div>
                                    <div class="cb-msg__body">
                                        <span class="cb-msg__text" x-text="msg.content"></span>
                                    </div>
                                </div>
                            </template>
                            <template x-if="msg.role === 'assistant'">
                                <div class="cb-msg__assistant-wrap">
                                    <div
                                        class="cb-msg__tool-activity"
                                        x-show="msg.toolActivity"
                                        x-text="msg.toolActivity"
                                        x-cloak
                                    ></div>
                                    <div
                                        class="cb-msg__body"
                                        :class="{ 'cb-msg__body--pending': msg.typing && !msg.content }"
                                    >
                                        <div
                                            class="cb-typing"
                                            x-show="msg.typing && !msg.content"
                                            x-cloak
                                            aria-label="Válasz készül"
                                        >
                                            <span class="cb-typing__dot"></span>
                                            <span class="cb-typing__dot"></span>
                                            <span class="cb-typing__dot"></span>
                                        </div>
                                        <div
                                            x-show="!(msg.typing && !msg.content)"
                                            x-html="formatAssistantMessage(msg)"
                                        ></div>
                                    </div>
                                    <div
                                        class="cb-commerce-media"
                                        x-show="hasCommerceMedia(msg) && !msg.typing"
                                        x-cloak
                                    >
                                        <template x-if="msg.commerceMedia?.layout === 'product'">
                                            <div class="cb-commerce-media__product">
                                                <template x-for="(item, mediaIndex) in msg.commerceMedia.items" :key="'p-' + mediaIndex">
                                                    <article class="cb-product-card">
                                                        <a
                                                            x-show="item.url"
                                                            class="cb-product-card__media cb-product-card__media--link"
                                                            :href="item.url"
                                                            :target="linkTarget(item.url)"
                                                            :rel="linkRel(item.url)"
                                                        >
                                                            <img
                                                                x-show="item.image_url"
                                                                class="cb-product-card__image"
                                                                :src="item.image_url"
                                                                :alt="item.image_alt || item.name || 'Termék kép'"
                                                                loading="lazy"
                                                            />
                                                            <div
                                                                x-show="!item.image_url"
                                                                class="cb-product-card__placeholder"
                                                                aria-hidden="true"
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                                                    <rect x="3" y="5" width="18" height="14" rx="2"/>
                                                                    <circle cx="8.5" cy="10.5" r="1.5"/>
                                                                    <path d="m21 16-5.5-5.5L9 17"/>
                                                                </svg>
                                                            </div>
                                                        </a>
                                                        <div
                                                            x-show="!item.url"
                                                            class="cb-product-card__media"
                                                        >
                                                            <img
                                                                x-show="item.image_url"
                                                                class="cb-product-card__image"
                                                                :src="item.image_url"
                                                                :alt="item.image_alt || item.name || 'Termék kép'"
                                                                loading="lazy"
                                                            />
                                                            <div
                                                                x-show="!item.image_url"
                                                                class="cb-product-card__placeholder"
                                                                aria-hidden="true"
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                                                    <rect x="3" y="5" width="18" height="14" rx="2"/>
                                                                    <circle cx="8.5" cy="10.5" r="1.5"/>
                                                                    <path d="m21 16-5.5-5.5L9 17"/>
                                                                </svg>
                                                            </div>
                                                        </div>
                                                        <div class="cb-product-card__body">
                                                            <h4
                                                                class="cb-product-card__title"
                                                                x-show="item.name"
                                                                x-text="item.name"
                                                            ></h4>
                                                            <div class="cb-product-card__meta">
                                                                <span
                                                                    class="cb-product-card__price"
                                                                    x-show="item.price_label"
                                                                    x-text="item.price_label"
                                                                ></span>
                                                                <span
                                                                    class="cb-product-card__stock"
                                                                    x-show="item.stock_label"
                                                                    :class="item.in_stock === true ? 'cb-product-card__stock--in' : (item.in_stock === false ? 'cb-product-card__stock--out' : '')"
                                                                    x-text="item.stock_label"
                                                                ></span>
                                                            </div>
                                                            <a
                                                                x-show="item.url"
                                                                class="cb-product-card__link"
                                                                :href="item.url"
                                                                :target="linkTarget(item.url)"
                                                                :rel="linkRel(item.url)"
                                                            >Terméklap megtekintése →</a>
                                                        </div>
                                                    </article>
                                                </template>
                                            </div>
                                        </template>
                                        <template x-if="msg.commerceMedia?.layout === 'catalog'">
                                            <div class="cb-commerce-media__catalog">
                                                <template x-for="(item, mediaIndex) in msg.commerceMedia.items" :key="'c-' + mediaIndex">
                                                    <article class="cb-catalog-card">
                                                        <a
                                                            x-show="item.url"
                                                            class="cb-catalog-card__link"
                                                            :href="item.url"
                                                            :target="linkTarget(item.url)"
                                                            :rel="linkRel(item.url)"
                                                            :title="item.name || 'Termék'"
                                                        >
                                                            <div class="cb-catalog-card__media">
                                                                <img
                                                                    x-show="item.image_url"
                                                                    class="cb-catalog-card__image"
                                                                    :src="item.image_url"
                                                                    :alt="item.image_alt || item.name || 'Termék'"
                                                                    loading="lazy"
                                                                />
                                                                <div
                                                                    x-show="!item.image_url"
                                                                    class="cb-catalog-card__placeholder"
                                                                    aria-hidden="true"
                                                                >
                                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                                                        <rect x="3" y="5" width="18" height="14" rx="2"/>
                                                                        <circle cx="8.5" cy="10.5" r="1.5"/>
                                                                        <path d="m21 16-5.5-5.5L9 17"/>
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                            <div class="cb-catalog-card__body">
                                                                <span
                                                                    class="cb-catalog-card__name"
                                                                    x-show="item.name"
                                                                    x-text="item.name"
                                                                ></span>
                                                                <span
                                                                    class="cb-catalog-card__price"
                                                                    x-show="item.price_label"
                                                                    x-text="item.price_label"
                                                                ></span>
                                                            </div>
                                                        </a>
                                                        <div
                                                            x-show="!item.url"
                                                            class="cb-catalog-card__static"
                                                        >
                                                            <div class="cb-catalog-card__media">
                                                                <img
                                                                    x-show="item.image_url"
                                                                    class="cb-catalog-card__image"
                                                                    :src="item.image_url"
                                                                    :alt="item.image_alt || item.name || 'Termék'"
                                                                    loading="lazy"
                                                                />
                                                                <div
                                                                    x-show="!item.image_url"
                                                                    class="cb-catalog-card__placeholder"
                                                                    aria-hidden="true"
                                                                >
                                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                                                                        <rect x="3" y="5" width="18" height="14" rx="2"/>
                                                                        <circle cx="8.5" cy="10.5" r="1.5"/>
                                                                        <path d="m21 16-5.5-5.5L9 17"/>
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                            <div class="cb-catalog-card__body">
                                                                <span
                                                                    class="cb-catalog-card__name"
                                                                    x-show="item.name"
                                                                    x-text="item.name"
                                                                ></span>
                                                                <span
                                                                    class="cb-catalog-card__price"
                                                                    x-show="item.price_label"
                                                                    x-text="item.price_label"
                                                                ></span>
                                                            </div>
                                                        </div>
                                                    </article>
                                                </template>
                                            </div>
                                        </template>
                                    </div>
                                    <p
                                        class="cb-source"
                                        x-show="showSourceAttribution && msg.sourceAttribution && !msg.typing"
                                        x-text="msg.sourceAttribution"
                                        x-cloak
                                    ></p>
                                    <div
                                        class="cb-feedback"
                                        x-show="canShowFeedback(msg)"
                                        x-cloak
                                    >
                                        <button
                                            type="button"
                                            class="cb-feedback__btn"
                                            :class="{ 'cb-feedback__btn--active': msg.feedback === 'positive' }"
                                            :disabled="hasSubmittedFeedback(msg) || msg.feedbackSubmitting || streaming"
                                            @click="submitFeedback(msg, 'positive')"
                                            aria-label="Hasznos válasz"
                                            title="Hasznos válasz"
                                        >👍</button>
                                        <button
                                            type="button"
                                            class="cb-feedback__btn"
                                            :class="{ 'cb-feedback__btn--active': msg.feedback === 'negative' }"
                                            :disabled="hasSubmittedFeedback(msg) || msg.feedbackSubmitting || streaming"
                                            @click="submitFeedback(msg, 'negative')"
                                            aria-label="Nem hasznos válasz"
                                            title="Nem hasznos válasz"
                                        >👎</button>
                                    </div>
                                </div>
                            </template>
                            <template x-if="msg.role === 'lead_form'">
                                <div class="cb-lead-form" :style="{ borderColor: primaryColor }">
                                    <div class="cb-lead-form__header">
                                        <h3 class="cb-lead-form__title" x-text="msg.payload.title"></h3>
                                        <button
                                            type="button"
                                            class="cb-lead-form__close"
                                            @click="dismissLeadFormMessage(msg)"
                                            aria-label="Bezárás"
                                        >×</button>
                                    </div>
                                    <template x-if="msg.state === 'success'">
                                        <div class="cb-lead-form__success">
                                            <p x-text="msg.successMessage"></p>
                                            <a
                                                x-show="msg.successUrl"
                                                :href="msg.successUrl"
                                                target="_blank"
                                                rel="noopener"
                                                x-text="msg.successUrlLabel || msg.successUrl"
                                            ></a>
                                        </div>
                                    </template>
                                    <template x-if="msg.state !== 'success'">
                                        <div class="cb-lead-form__body">
                                            <p class="cb-lead-form__hint" x-show="msg.hasPrefill">Előre kitöltöttem, amit a beszélgetésből tudok — nézd át, mielőtt elküldöd.</p>
                                            <div class="cb-lead-form__fields">
                                                <template x-for="field in msg.payload.fields" :key="field.id">
                                                    <label class="cb-lead-form__field">
                                                        <span class="cb-lead-form__label" x-text="field.label + (field.required ? ' *' : '')"></span>
                                                        <template x-if="field.type === 'textarea'">
                                                            <textarea
                                                                class="cb-lead-form__input"
                                                                rows="3"
                                                                :placeholder="field.placeholder || ''"
                                                                x-model="msg.fieldValues[field.id]"
                                                                :disabled="msg.state === 'submitting'"
                                                            ></textarea>
                                                        </template>
                                                        <template x-if="field.type === 'select'">
                                                            <select
                                                                class="cb-lead-form__input"
                                                                x-model="msg.fieldValues[field.id]"
                                                                :disabled="msg.state === 'submitting'"
                                                            >
                                                                <option value="">Válassz…</option>
                                                                <template x-for="opt in (field.options || [])" :key="opt">
                                                                    <option :value="opt" x-text="opt"></option>
                                                                </template>
                                                            </select>
                                                        </template>
                                                        <template x-if="field.type !== 'textarea' && field.type !== 'select'">
                                                            <input
                                                                class="cb-lead-form__input"
                                                                :type="field.type === 'email' ? 'email' : (field.type === 'phone' ? 'tel' : 'text')"
                                                                :placeholder="field.placeholder || ''"
                                                                x-model="msg.fieldValues[field.id]"
                                                                :disabled="msg.state === 'submitting'"
                                                            />
                                                        </template>
                                                    </label>
                                                </template>
                                                <label class="cb-lead-form__consent" x-show="msg.payload.consent_required !== false">
                                                    <input
                                                        type="checkbox"
                                                        x-model="msg.consentGiven"
                                                        :disabled="msg.state === 'submitting'"
                                                    />
                                                    <span>
                                                        <span x-text="msg.payload.gdpr_text"></span>
                                                        <a
                                                            x-show="msg.payload.gdpr_url"
                                                            :href="msg.payload.gdpr_url"
                                                            target="_blank"
                                                            rel="noopener"
                                                        >Adatkezelési tájékoztató</a>
                                                    </span>
                                                </label>
                                            </div>
                                            <p class="cb-lead-form__error" x-show="msg.state === 'error'" x-text="msg.errorMessage"></p>
                                            <button
                                                type="button"
                                                class="cb-lead-form__submit"
                                                :style="{ backgroundColor: primaryColor }"
                                                :disabled="!canSubmitLeadForm(msg) || msg.state === 'submitting'"
                                                @click="submitLeadFormMessage(msg)"
                                            >
                                                <span x-show="msg.state !== 'submitting'" x-text="msg.payload.submit_label || 'Küldés'"></span>
                                                <span x-show="msg.state === 'submitting'">Küldés…</span>
                                            </button>
                                        </div>
                                    </template>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>
            </div>

            <div x-show="error" class="cb-error">
                <span x-text="error"></span>
                <template x-if="upgradeUrl">
                    <a :href="upgradeUrl" target="_blank" rel="noopener" class="cb-error__upgrade">Csomag váltás →</a>
                </template>
            </div>

            <div class="cb-panel__footer">
                <button
                    type="button"
                    class="cb-mic"
                    x-show="voiceEnabled"
                    x-cloak
                    @click="toggleVoice()"
                    :disabled="false"
                    :class="{
                        'cb-mic--active': voiceCallActive,
                        'cb-mic--listening': voiceState === 'listening',
                        'cb-mic--processing': voiceState === 'processing' || voiceState === 'connecting',
                        'cb-mic--speaking': voiceState === 'speaking',
                    }"
                    :aria-label="voiceCallActive ? 'Beszélgetés leállítása' : 'Hangos beszélgetés indítása'"
                    :title="voiceCallActive
                        ? (voiceState === 'connecting'
                            ? 'Kapcsolódás… újra megnyomva leáll'
                            : 'Beszélgetés leállítása (újra megnyomva)')
                        : (voicePipecatEnabled
                            ? 'Egy kattintás: élő hanghívás. Beszélj — a bot válaszol. Újra: vége.'
                            : 'Nyomd meg, beszélj — a csend után válaszol hanggal')"
                >
                    <svg
                        x-show="voiceState === 'connecting'"
                        class="cb-mic__spinner"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        width="18"
                        height="18"
                        aria-hidden="true"
                    >
                        <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" opacity="0.25"/>
                        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                    </svg>
                    <svg
                        x-show="voiceState !== 'connecting'"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        width="18"
                        height="18"
                        aria-hidden="true"
                    >
                        <path d="M12 14a3 3 0 0 0 3-3V7a3 3 0 1 0-6 0v4a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"/>
                    </svg>
                </button>
                <textarea
                    class="cb-input"
                    x-ref="input"
                    rows="1"
                    :placeholder="voiceState === 'listening'
                        ? (voicePipecatEnabled ? 'Hallgatlak… beszélj nyugodtan' : 'Hallgatlak… beszélj, a csend után küldöm')
                        : (voiceState === 'processing' || voiceState === 'connecting'
                            ? 'Hang kapcsolódása…'
                            : (voiceState === 'speaking'
                                ? 'A bot beszél…'
                                : 'Írd ide az üzeneted…'))"
                    x-model="input"
                    @keydown="onKeydown($event)"
                    :disabled="streaming"
                ></textarea>
                <button type="button" class="cb-send" @click="send()" :disabled="streaming || !input.trim()" aria-label="Küldés" title="Küldés">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
                        <path d="M3.4 20.4 20.85 12.92c.7-.3.7-1.54 0-1.84L3.4 3.6a1.05 1.05 0 0 0-1.5 1.2l2.1 6.5a.7.7 0 0 0 .55.48l8.05.9-8.05.9a.7.7 0 0 0-.55.48l-2.1 6.5a1.05 1.05 0 0 0 1.5 1.2Z"/>
                    </svg>
                </button>
            </div>

            <div class="cb-branding" x-show="showBranding">
                <a :href="appOrigin" target="_blank" rel="noopener noreferrer" class="cb-branding__link">
                    <span class="cb-branding__label">Powered by</span>
                    <img
                        class="cb-branding__logo cb-branding__logo--light"
                        x-show="!darkMode"
                        :src="brandLogoLightUrl"
                        alt="chatt.hu"
                        width="160"
                        height="32"
                        loading="lazy"
                    />
                    <img
                        class="cb-branding__logo cb-branding__logo--dark"
                        x-show="darkMode"
                        :src="brandLogoDarkUrl"
                        alt="chatt.hu"
                        width="160"
                        height="32"
                        loading="lazy"
                    />
                </a>
            </div>
        </div>
    </div>
    `;
}
